import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DashboardModal from '@/components/dashboard-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUserLibraryCategories, useCategoryMutations, LibraryCategory } from '@/hooks/universal-storage/use-library-categories';
import { useForm } from 'react-hook-form';
import { CircleDot, Plus, Trash2, Edit, Edit2, X, Loader2, Palette, Smile } from 'lucide-react';
import ColorPicker, { getColorClass } from '@/components/ui/ColorPicker';
import { ColorVariant } from '@/components/ui/SidebarFilterButtons';
import IconPicker, { getCategoryIcon, type CategoryIcon } from '@/components/ui/IconPicker';

interface CategoryManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type FormValues = {
  name: string;
};

const CategoryManagementModal: React.FC<CategoryManagementModalProps> = ({ isOpen, onClose }) => {
    const { data: categories, isLoading } = useUserLibraryCategories();
    const { createCategory, updateCategory, deleteCategory, isPending } = useCategoryMutations();
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [colorPickerOpen, setColorPickerOpen] = useState<string | null>(null);
    const [iconPickerOpen, setIconPickerOpen] = useState<string | null>(null);

    const { register, handleSubmit, reset, setValue, formState: { errors, isValid } } = useForm<FormValues>();

    useEffect(() => {
        if (!isOpen) {
            setEditingCategoryId(null);
            setIsCreating(false);
            setColorPickerOpen(null);
            setIconPickerOpen(null);
            reset({ name: '' });
        }
    }, [isOpen, reset]);

    const handleSave = async (values: FormValues) => {
        if (isPending) return;
        try {
            if (editingCategoryId) {
                await updateCategory({ id: editingCategoryId, updates: values });
            } else if (isCreating) {
                await createCategory({ name: values.name, content_type: null, color: 'purple', icon: 'folder' });
            }
            cancelForm();
        } catch (error) {
            // Toast is handled in the hook
        }
    };
    
    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this folder? This cannot be undone.')) {
            await deleteCategory(id);
        }
    };

    const handleColorChange = async (categoryId: string, color: ColorVariant) => {
        await updateCategory({ id: categoryId, updates: { color } });
        setColorPickerOpen(null);
    };

    const handleIconChange = async (categoryId: string, icon: CategoryIcon) => {
        await updateCategory({ id: categoryId, updates: { icon } });
        setIconPickerOpen(null);
    };

    const startEditing = (category: LibraryCategory) => {
        setIsCreating(false);
        setEditingCategoryId(category.id);
        setValue('name', category.name, { shouldValidate: true });
    };

    const startCreating = () => {
        setEditingCategoryId(null);
        setIsCreating(true);
        reset({ name: '' });
    };
    
    const cancelForm = () => {
        setEditingCategoryId(null);
        setIsCreating(false);
        reset({ name: '' });
    };

    const renderForm = (category?: LibraryCategory) => (
        <form onSubmit={handleSubmit(handleSave)} className="flex items-center gap-2 p-3 bg-gradient-to-r from-accent/5 to-accent/10 rounded-lg border border-accent/20">
            <div className="relative w-full">
                <Input 
                    {...register('name', { required: 'Name is required.' })} 
                    placeholder="Folder name..."
                    autoFocus
                    className="bg-background/50 border-accent/30 focus:border-accent/50"
                />
                {errors.name && <p className="text-xs text-red-500 absolute -bottom-5 left-1">{errors.name.message}</p>}
            </div>
            <motion.button
                type="submit"
                disabled={!isValid || isPending}
                whileHover={{ scale: isPending ? 1 : 1.05 }}
                whileTap={{ scale: isPending ? 1 : 0.95 }}
                className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-md border border-transparent transition-colors text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30 hover:border-green-200 dark:hover:border-green-800/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isPending ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Save'}
            </motion.button>
            <motion.button
                type="button"
                onClick={cancelForm}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="shrink-0 h-8 w-8 flex items-center justify-center rounded-md border border-transparent transition-colors text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950/30 hover:border-gray-200 dark:hover:border-gray-800/50"
            >
                <X size={16}/>
            </motion.button>
        </form>
    );

    return (
        <DashboardModal
            isOpen={isOpen}
            onClose={onClose}
            title="Manage Storage"
            size="md"
            accentColor="purple"
        >
            <div 
                className="space-y-4 min-h-[200px]"
                onClick={(e) => e.stopPropagation()}
                onWheel={(e) => e.stopPropagation()}
            >
                {isLoading && (
                    <div className="flex justify-center items-center h-32">
                        <Loader2 className="h-6 w-6 animate-spin text-purple-500"/>
                    </div>
                )}
                
                <div className="space-y-3">
                    {categories?.map(cat => (
                        <div key={cat.id}>
                            {editingCategoryId === cat.id ? renderForm(cat) : (
                                <div className="relative">
                                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-accent/5 dark:hover:bg-accent/10 group transition-colors border border-transparent hover:border-accent/20">
                                        <div className="flex items-center gap-3">
                                            {(() => {
                                                const IconComponent = getCategoryIcon((cat.icon as CategoryIcon) || 'folder');
                                                return <IconComponent className={`h-5 w-5 transition-colors ${getColorClass(cat.color as ColorVariant)}`} />;
                                            })()}
                                            <span className="font-medium">{cat.name}</span>
                                        </div>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.95 }}
                                                className="p-1.5 rounded-md hover:bg-accent/10 text-muted-foreground hover:text-foreground transition-colors"
                                                onClick={() => setEditingCategoryId(cat.id)}
                                            >
                                                <Edit2 className="h-4 w-4" />
                                            </motion.button>
                                            {categories.length > 1 && (
                                                <motion.button
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                                    onClick={() => handleDeleteCategory(cat.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </motion.button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {isCreating ? renderForm() : (
                    <Button 
                        variant="outline" 
                        className="w-full mt-4 border-dashed border-accent/30 hover:border-accent/50 hover:bg-accent/5" 
                        onClick={startCreating} 
                        disabled={isLoading}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        New Folder
                    </Button>
                )}
                
                {/* Inline Footer Button */}
                <div className="flex justify-center pt-4">
                    <Button onClick={onClose} variant="outline">
                        Done
                    </Button>
                </div>
            </div>
        </DashboardModal>
    );
};

export default CategoryManagementModal;
